"use client";
import { FC, useCallback, useState } from "react";
import { handleRedirection } from "../../utils/helper";
import { StockContent } from "./StockContent";
import { StockCardProps } from "./types";
import { useRouter } from "next/navigation";

export const StockInfoCard: FC<StockCardProps> = ({
  stock,
  defaultImage,
  fullWidth = false,
  modalTitle,
  modalBtnCta,
  successMsg,
  errorMsg,
  onCardClick,
  section_title,
  cta_text,
  hidePriceChange = false,
  isCardClickable = true,
  disableDefaultRedirection = false,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const router = useRouter();

  const handleCardClick = useCallback(
    (e: React.MouseEvent) => {
      if (!isCardClickable) {
        return;
      }

      if (isModalOpen) {
        return;
      }

      const target = e.target as HTMLElement;
      if (
        target.closest('button[aria-label="Add to watchlist"]') ||
        target.closest(".icomoon")
      ) {
        return;
      }

      onCardClick?.(stock);

      if (!disableDefaultRedirection) {
        handleRedirection(`/${stock.redirectURL}`, router);
      }
    },
    [
      disableDefaultRedirection,
      isCardClickable,
      isModalOpen,
      onCardClick,
      router,
      stock,
    ],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isCardClickable || isModalOpen) {
        return;
      }

      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onCardClick?.(stock);
        if (!disableDefaultRedirection) {
          handleRedirection(`/${stock.redirectURL}`, router);
        }
      }
    },
    [
      disableDefaultRedirection,
      isCardClickable,
      isModalOpen,
      onCardClick,
      router,
      stock,
    ],
  );

  return (
    <div
      onClick={handleCardClick}
      onKeyDown={isCardClickable ? handleKeyDown : undefined}
      role={isCardClickable ? "button" : undefined}
      tabIndex={isCardClickable ? 0 : undefined}
      aria-label={
        isCardClickable
          ? `View details for ${stock.fullName || stock.symbol || "stock"}`
          : undefined
      }
    >
      <div
        className={`inline-flex min-h-[143px] w-full min-w-[156px] items-start justify-start rounded-[8px] border border-[#e5e5e5] bg-white p-3 md:min-w-[215px] ${
          isCardClickable ? "" : "cursor-default"
        }`}
      >
        <div className="w-full">
          <div className="flex flex-col">
            <StockContent
              stock={stock}
              defaultImage={defaultImage}
              onModalStateChange={setIsModalOpen}
              modalTitle={modalTitle}
              modalBtnCta={modalBtnCta}
              successMsg={successMsg}
              errorMsg={errorMsg}
              cta_text={cta_text}
              section_title={section_title}
              hidePriceChange={hidePriceChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockInfoCard;
